import { LOINC_DISPLAY_NAMES_ES, LOINC_LAB_DISPLAY_NAMES } from '@liza/fhir-utils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { ValueSetCode } from '../components/records/ValueSetCombobox';
import { LOINC_SYSTEM } from '../utils/observation';
import type { UseValueSetExpandResult } from './useValueSetExpand';

/**
 * Local drop-in replacement for `useValueSetExpand` against the primary-care labs ValueSet.
 * Returns the curated labs with their friendly display names and filters them client-side, so it
 * matches `UseValueSetExpandResult` and slots into the same combobox wiring with no network call.
 * Uses Spanish labels when the app language is 'es'.
 *
 * We build the picker locally rather than via a server `$expand` so the dropdown both *shows* and
 * *searches by* the friendly names. A `$expand` returns the LOINC CodeSystem's standard display
 * (e.g. "Glucose [Mass/volume] in Serum or Plasma") and filters against it, so friendly names
 * would neither render (on LOINC-loaded servers) nor be searchable.
 */
export function useCuratedLabItems(): UseValueSetExpandResult {
  const { i18n } = useTranslation();
  const [query, setQuery] = useState('');
  // Debounced to match `useValueSetExpand`'s 250ms cadence so that, in the grouped measurement
  // combobox, the labs group doesn't filter a step ahead of the server-driven vitals/surveys.
  const onQueryChange = useDebouncedCallback(setQuery, 250);

  const allLabItems = useMemo((): ValueSetCode[] => {
    const isEs = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es');
    return Object.entries(LOINC_LAB_DISPLAY_NAMES)
      .map(([code, enDisplay]) => ({
        system: LOINC_SYSTEM,
        code,
        display: isEs ? (LOINC_DISPLAY_NAMES_ES[code] ?? enDisplay) : enDisplay,
      }))
      .sort((a, b) => (a.display ?? '').localeCompare(b.display ?? '', i18n.language));
  }, [i18n.language, i18n.resolvedLanguage]);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allLabItems;
    return allLabItems.filter((item) => item.display?.toLowerCase().includes(q) || item.code.includes(q));
  }, [query, allLabItems]);

  return { items, onQueryChange, isFetching: false, error: undefined };
}
